import { Button } from '@/components/ui-kit/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { LayoutGrid, ListOrdered } from 'lucide-react';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { ToolsList } from './tools-list';
import { BlocksToolsList } from './blocks-tools-list';
import { AddMcpServerModal } from '@/modules/gpt-chats/components/tools/add-mcp-server-modal';
import { AddToolModal } from '@/modules/gpt-chats/components/tools/add-tool-modal';
import { useState } from 'react';
import { useGetTools } from '@/modules/gpt-chats/hooks/use-tools';
import { useTranslation } from 'react-i18next';

export const Tools = () => {
  const { t } = useTranslation();
  const [queries, setQueries] = useQueryStates({
    view: parseAsString.withDefault('grid'),
    offset: parseAsInteger.withDefault(0),
    limit: parseAsInteger.withDefault(50),
    name: parseAsString.withDefault(''),
    toolType: parseAsString.withDefault('tool'),
    blocksToolType: parseAsString.withDefault('all'),
    blocksToolStatus: parseAsString.withDefault('all'),
  });
  const [isAddMcpServerModalOpen, setIsAddMcpServerModalOpen] = useState(false);
  const [isAddToolModalOpen, setIsAddToolModalOpen] = useState(false);

  const { data } = useGetTools({});

  const apiToolsCount = data?.tools?.filter((tool) => tool.type === 'api')?.length ?? 0;
  const mcpServerCount = data?.tools?.filter((tool) => tool.type === 'mcp_server')?.length ?? 0;
  const currentCount = queries.toolType === 'mcp-server' ? mcpServerCount : apiToolsCount;

  const userApiToolsCount =
    data?.tools?.filter((tool) => tool.type === 'api' && tool.is_open_tool === false)?.length ?? 0;
  const userMcpServerCount =
    data?.tools?.filter((tool) => tool.type === 'mcp_server' && tool.is_open_tool === false)
      ?.length ?? 0;
  const userToolsCount = queries.toolType === 'mcp-server' ? userMcpServerCount : userApiToolsCount;

  const handleAddClick = () => {
    if (queries.toolType === 'mcp-server') {
      setIsAddMcpServerModalOpen(true);
    } else if (queries.toolType === 'tool') {
      setIsAddToolModalOpen(true);
    }
  };

  const showAddButton =
    queries.toolType !== 'blocks-tools' && currentCount > 0 && userToolsCount < 50;
  const showLimitMessage =
    queries.toolType !== 'blocks-tools' &&
    ((queries.toolType === 'mcp-server' && userMcpServerCount >= 5) ||
      (queries.toolType === 'tool' && userApiToolsCount >= 5));

  return (
    <>
      <section className="flex flex-col gap-6">
        <div className="flex min-h-10 items-center gap-3">
          <h3 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            {t('TOOLS_TITLE')}
          </h3>
        </div>

        <div className="flex items-center justify-between">
          <Tabs
            value={queries.toolType}
            onValueChange={(v) => setQueries((prev) => ({ ...prev, toolType: v, offset: 0 }))}
          >
            <TabsList>
              <TabsTrigger value="tool">{t('TOOL_TAB')}</TabsTrigger>
              <TabsTrigger value="mcp-server">{t('MCP_TAB')}</TabsTrigger>
              <TabsTrigger value="blocks-tools">{t('BLOCKS_TOOLS_TAB')}</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex items-center gap-4">
            {queries.toolType === 'blocks-tools' && (
              <>
                <Select
                  value={queries.blocksToolType}
                  onValueChange={(v) =>
                    setQueries((prev) => ({ ...prev, blocksToolType: v, offset: 0 }))
                  }
                >
                  <SelectTrigger className="w-[150px] lg:w-[180px]">
                    <SelectValue placeholder={t('TOOL_TYPE_PLACEHOLDER')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('NONE')}</SelectItem>
                    <SelectItem value="api">{t('API')}</SelectItem>
                    <SelectItem value="mcp_server">{t('MCP_SERVER')}</SelectItem>
                    <SelectItem value="webhook">{t('WEBHOOK')}</SelectItem>
                    <SelectItem value="graphql">{t('GRAPHQL')}</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={queries.blocksToolStatus}
                  onValueChange={(v) =>
                    setQueries((prev) => ({ ...prev, blocksToolStatus: v, offset: 0 }))
                  }
                >
                  <SelectTrigger className="w-[150px] lg:w-[180px]">
                    <SelectValue placeholder={t('STATUS_PLACEHOLDER')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('NONE')}</SelectItem>
                    <SelectItem value="active">{t('ACTIVE')}</SelectItem>
                    <SelectItem value="inactive">{t('INACTIVE')}</SelectItem>
                    <SelectItem value="deprecated">{t('DEPRECATED')}</SelectItem>
                    <SelectItem value="maintenance">{t('MAINTENANCE')}</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
            {showAddButton && <Button onClick={handleAddClick}>{t('ADD')}</Button>}
            {!showAddButton && showLimitMessage && (
              <span className="text-sm text-medium-emphasis">{t('TOOL_LIMIT_MESSAGE')}</span>
            )}
            <Tabs
              value={queries.view}
              onValueChange={(v) => setQueries((prev) => ({ ...prev, view: v }))}
              className="hidden sm:block"
            >
              <TabsList>
                <TabsTrigger value="list">
                  <ListOrdered className="aspect-square w-4" />
                </TabsTrigger>
                <TabsTrigger value="grid">
                  <LayoutGrid className="aspect-square w-4" />
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setQueries((prev) => ({ ...prev, view: prev.view === 'list' ? 'grid' : 'list' }))
              }
              className="sm:hidden"
            >
              {queries.view === 'list' ? (
                <LayoutGrid className="aspect-square w-4" />
              ) : (
                <ListOrdered className="aspect-square w-4" />
              )}
            </Button>
          </div>
        </div>

        {queries.toolType === 'blocks-tools' ? (
          <BlocksToolsList
            offset={queries.offset}
            limit={10}
            view={queries.view}
            toolType={queries.blocksToolType === 'all' ? null : queries.blocksToolType}
            toolStatus={queries.blocksToolStatus === 'all' ? null : queries.blocksToolStatus}
            onOffsetChange={(offset: number) => setQueries((prev) => ({ ...prev, offset }))}
            onLimitChange={(limit: number) => setQueries((prev) => ({ ...prev, limit, offset: 0 }))}
          />
        ) : (
          <ToolsList
            offset={queries.offset}
            limit={queries.limit}
            name={queries.name}
            view={queries.view}
            toolType={queries.toolType}
            onAddClick={handleAddClick}
            onOffsetChange={(offset: number) => setQueries((prev) => ({ ...prev, offset }))}
            onLimitChange={(limit: number) => setQueries((prev) => ({ ...prev, limit, offset: 0 }))}
          />
        )}
      </section>

      <AddToolModal open={isAddToolModalOpen} onOpenChange={setIsAddToolModalOpen} />
      <AddMcpServerModal open={isAddMcpServerModalOpen} onOpenChange={setIsAddMcpServerModalOpen} />
    </>
  );
};
